import cx from 'clsx';
import PropTypes from 'prop-types';
import Tree from 'rc-tree';
import React, { useEffect, useRef, useState } from 'react';
import { useController } from 'react-hook-form';

import { ReactComponent as ThinArrowIcon } from '@assets/svg/thin-arrow.svg';

import { useClickAway } from '@hooks/useClickAway';
import useTranslation from '@hooks/useTranslation';

import { filterTree } from './helper/handleExpandedItems.ts';
import style from './style.module.scss';

// TODO: Will be changed to TS
const propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultSelectedName: PropTypes.string,
  selectedName: PropTypes.string,
  onChange: PropTypes.func,
  error: PropTypes.any,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  items: PropTypes.array,
  fieldNames: PropTypes.shape({
    children: PropTypes.string,
    title: PropTypes.string,
    key: PropTypes.string,
  }),
  control: PropTypes.any,
  searchable: PropTypes.bool,
  name: PropTypes.string,
  defaultExpandAll: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  variant: PropTypes.string,
  type: PropTypes.string,
  isDirty: PropTypes.bool,
};

const defaultFieldNames = {
  children: 'children',
  title: 'title',
  key: 'key',
};

const Component = ({
  value,
  defaultSelectedName,
  selectedName,
  onChange,
  error,
  label,
  placeholder,
  items = [],
  fieldNames,
  searchable = false,
  name,
  defaultExpandAll = false,
  required = false,
  disabled,
  variant,
  type,
  isDirty = false,
}) => {
  const ref = useRef(null);
  const { t } = useTranslation();

  const [openTree, setOpenTree] = useState(false);
  const [treeData, setTreeData] = useState(items);
  const [selectValueName, setSelectValueName] = useState(defaultSelectedName);
  const [searchValue, setSearchValue] = useState('');
  const [expandedKeysState, setExpandedKeysState] = useState([]);

  const currentFieldNames = fieldNames || defaultFieldNames;
  const inputValue = searchable && openTree ? searchValue : selectedName || selectValueName || '';

  useEffect(() => {
    setTreeData(items);
  }, [items]);

  useClickAway(ref, () => setOpenTree(false), true);

  useEffect(() => {
    if (defaultSelectedName && !isDirty) {
      setSelectValueName(defaultSelectedName);
    }
  }, [defaultSelectedName, isDirty]);

  const handleInputChange = (e) => {
    const searchValue = e.target.value;
    const { filteredItems, expandedKeys } = filterTree(items, searchValue);

    setTreeData(filteredItems);
    setExpandedKeysState(searchValue ? expandedKeys : []);
    setSearchValue(searchValue);
  };

  const handleTreeSelect = (_, info) => {
    onChange(info.node[currentFieldNames.key], info.node);
    setSelectValueName(info.node[currentFieldNames.title]);
    setSearchValue(info.node[currentFieldNames.title]);
    setExpandedKeysState([]);
    setOpenTree(false);

    if (searchValue) {
      setTreeData(items);
    }
  };

  return (
    <div className={style.treeSelectWrapper} ref={ref}>
      {label && (
        <label className={cx(style.label, { [style.required]: required })} htmlFor={name}>
          {label}
        </label>
      )}
      <div className={style.inputWrapper}>
        <input
          type="secondary"
          className={cx(style.treeSelectInput, {
            [style.headerTreeselectInput]: variant === 'header-treeselect',
          })}
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          onClick={() => setOpenTree(!openTree)}
          onChange={handleInputChange}
          readOnly={!searchable || !openTree}
        />
        <ThinArrowIcon className={style.inputArrow} />
      </div>
      {type !== 'woHelperText' && <div className={style.helperText}>{error && t(error?.message)}</div>}
      {openTree && (
        <div className={style.treeWrapper}>
          {treeData.length ? (
            <Tree
              className={cx(style.treeComponent, {
                [style.headerTreeComponent]: variant === 'header-treeselect',
              })}
              treeData={treeData}
              selectedKeys={[value]}
              selectable={true}
              checkable={false}
              showLine={false}
              showIcon={false}
              multiple={false}
              fieldNames={currentFieldNames}
              switcherIcon={<ThinArrowIcon />}
              expandedKeys={expandedKeysState}
              autoExpandParent={false}
              onExpand={(keys) => setExpandedKeysState(keys)}
              defaultExpandAll={defaultExpandAll}
              onSelect={handleTreeSelect}
            />
          ) : (
            <span className={style.noDataFound}>{t('No data found')}</span>
          )}
        </div>
      )}
    </div>
  );
};

const Controlled = ({
  items,
  name,
  searchable,
  control,
  selectedName,
  defaultSelectedName,
  label,
  fieldNames,
  placeholder,
  defaultExpandAll,
  required,
  variant,
  type,
}) => {
  const {
    field: { value, onChange, disabled },
    fieldState: { error, isDirty },
  } = useController({ control, name });

  return (
    <Component
      value={value}
      defaultSelectedName={defaultSelectedName}
      selectedName={selectedName}
      onChange={onChange}
      error={error}
      label={label}
      placeholder={placeholder}
      items={items}
      searchable={searchable}
      fieldNames={fieldNames}
      name={name}
      defaultExpandAll={defaultExpandAll}
      required={required}
      disabled={disabled}
      variant={variant}
      type={type}
      isDirty={isDirty}
    />
  );
};

const TreeSelect = (props) => {
  return props.control ? <Controlled {...props} /> : <Component {...props} />;
};

TreeSelect.propTypes = propTypes;

export default TreeSelect;
